import { normalizeInstanceUrl } from '../instance/config.ts';

/**
 * The PeerTube fallback — §8's Integrations tab, the idea `integrations.md` named and never
 * built until now. Two players behind one switch:
 *
 * - **`integrated`** (default): the matched PeerTube video's HLS manifest becomes the *Shaka*
 *   player's source, over p2p-media-loader-shaka — our own headless chrome stays, only where the
 *   bytes come from changes.
 * - **`embedded`**: PeerTube's own `<iframe>` embed, its own chrome — the same trade-off the
 *   YouTube escape hatch always makes, offered here as a choice because PeerTube's embed is a
 *   lighter, non-P2P fallback for a reader who would rather not open a WebRTC swarm.
 */

export type PeerTubePlayerMode = 'integrated' | 'embedded';

/**
 * PeerTube's own `searchTarget` query parameter — `local` asks the configured instance for videos
 * it knows about only, `search-index` asks it to also forward to whatever federated search index it
 * is configured with. Not every instance accepts the second value: an instance with no search index
 * configured answers `search-index` with a `400` (measured 2026-08-03, framatube.org) rather than
 * falling back silently, which the fetch's own error handling already turns into "no match" —
 * exactly why `sepiasearch.org`, a dedicated search index that always accepts it, is the default
 * rather than a normal instance opted into a mode most instances refuse.
 */
export type PeerTubeSearchTarget = 'local' | 'search-index';

export interface PeerTubeSettings {
  /** Off by default: enabling it can search a third-party instance for a matching video. */
  readonly enabled: boolean;
  /** The instance asked to search; `null` is the default, sepiasearch.org. Not necessarily where a
   *  matched video actually lives — see `PeerTubeSearchResult.instanceHost`. */
  readonly apiUrl: string | null;
  readonly mode: PeerTubePlayerMode;
  readonly searchTarget: PeerTubeSearchTarget;
}

export const PEERTUBE_DEFAULT_API_URL = 'https://sepiasearch.org';

export const DEFAULT_PEERTUBE_SETTINGS: PeerTubeSettings = {
  enabled: false,
  apiUrl: null,
  mode: 'integrated',
  searchTarget: 'search-index',
};

/** A stored service address → a canonical base URL or `null` (the default instance). */
export function normalizePeerTubeApiUrl(input: unknown): string | null {
  if (typeof input !== 'string' || input.trim() === '') return null;
  const result = normalizeInstanceUrl(input);
  return result.ok ? result.value : null;
}

function normalizePeerTubeMode(input: unknown): PeerTubePlayerMode {
  return input === 'embedded' ? 'embedded' : 'integrated';
}

function normalizePeerTubeSearchTarget(input: unknown): PeerTubeSearchTarget {
  return input === 'local' ? 'local' : 'search-index';
}

/** Total (R4): the input is a stored blob or a hand-edited one. */
export function normalizePeerTubeSettings(input: unknown): PeerTubeSettings {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return DEFAULT_PEERTUBE_SETTINGS;
  }
  const record = input as Readonly<Record<string, unknown>>;
  return {
    enabled: record['enabled'] === true,
    apiUrl: normalizePeerTubeApiUrl(record['apiUrl']),
    mode: normalizePeerTubeMode(record['mode']),
    searchTarget: normalizePeerTubeSearchTarget(record['searchTarget']),
  };
}

/** `count=25`: enough candidates for an exact title to be among them without a second page. */
const SEARCH_RESULT_COUNT = 25;

/** `null` for a blank title — searching for nothing is not a lookup. */
export function peerTubeSearchUrl(settings: PeerTubeSettings, title: string): string | null {
  const trimmed = title.trim();
  if (trimmed === '') return null;
  const base = settings.apiUrl ?? PEERTUBE_DEFAULT_API_URL;
  return `${base}/api/v1/search/videos?search=${encodeURIComponent(trimmed)}&count=${SEARCH_RESULT_COUNT}&searchTarget=${settings.searchTarget}`;
}

/**
 * The full video record — `streamingPlaylists[].playlistUrl` is what the "integrated" mode wants.
 * `instanceHost` is the *matched result's own* host, not the searched instance's: a `search-index`
 * hit routinely lives on a different server than the one asked to find it (measured 2026-08-03,
 * sepiasearch.org — every federated hit carries its origin's `account.host`), and this instance's
 * API is the only one that has the record at all.
 */
export function peerTubeVideoDetailsUrl(instanceHost: string, uuid: string): string | null {
  if (instanceHost.trim() === '' || uuid.trim() === '') return null;
  return `https://${instanceHost}/api/v1/videos/${encodeURIComponent(uuid)}`;
}

/**
 * The matched video's own **watch page** — where the "Watch on" menu's PeerTube row lands, the
 * counterpart of the embed address below. The result's `url` wins when it is there, and the
 * `https://{host}/videos/watch/{uuid}` form stands in when it is not, which is the same address the
 * payload carries (measured 2026-08-06, and that path answers `200` rather than redirecting).
 *
 * `?start=` in whole seconds, only for a positive playhead: PeerTube's watch page reads it the way
 * its embed does (measured 2026-08-06 against `video.blender.org` — `?start=61` opened the video at
 * 61 s). No `autoplay`: a watch page is somewhere a reader arrives, and PeerTube's own preference
 * decides whether it plays.
 */
export function peerTubeWatchUrl(
  match: { readonly instanceHost: string; readonly uuid: string; readonly watchUrl: string | null },
  startSec: number | null,
): string | null {
  const base =
    match.watchUrl ??
    (match.instanceHost.trim() === '' || match.uuid.trim() === ''
      ? null
      : `https://${match.instanceHost}/videos/watch/${encodeURIComponent(match.uuid)}`);
  if (base === null) return null;
  const start = startSec !== null && Number.isFinite(startSec) && startSec > 0 ? Math.floor(startSec) : null;
  return start === null ? base : `${base}?start=${start}`;
}

/**
 * The "embedded" mode's `<iframe>` source. `embedUrl` — the result's own absolute embed address,
 * present on a federated hit — wins when it exists; otherwise `embedPath` (instance-relative) is
 * joined onto `instanceHost`, the shape a same-instance ("local") hit carries instead. Plain whole
 * seconds for the playhead (measured 2026-08-03: `?start=30` against a real embed answers `200`),
 * matching `duration`'s own unit rather than a duration-string spelling.
 */
export function peerTubeEmbedUrl(
  match: { readonly instanceHost: string; readonly embedPath: string; readonly embedUrl: string | null },
  startSec: number | null,
): string | null {
  const base =
    match.embedUrl ??
    (match.instanceHost.trim() === '' || match.embedPath.trim() === ''
      ? null
      : `https://${match.instanceHost}${match.embedPath}`);
  if (base === null) return null;
  const start = startSec !== null && Number.isFinite(startSec) && startSec > 0 ? Math.floor(startSec) : null;
  const params = ['autoplay=1', ...(start === null ? [] : [`start=${start}`])];
  return `${base}?${params.join('&')}`;
}
